// Direkt aus der Datei, nicht aus `lib/dataSource` (index): Der zieht die Dexie-Quelle mit — die Mobile-App
// folgt auch Typ-Importen (`sharedPurity.test.ts`).
import type { DataSource } from '../dataSource/dataSource'
import type { LifeSpace, Task, TaskChanges } from '../db/models'
import { computeAppendOrders } from '../db/appendOrders'

/** Wohin **Verschieben** (#257) eine Aufgabe bringt: ein Projekt, wahlweise ein Abschnitt darin. */
export interface MoveTarget {
  projectId: string
  sectionId: string | null
  lifeSpace: LifeSpace
}

export interface MoveResult {
  moved: Task[]
  /** Der alte Platz — `updateTasks(before)` nimmt das Verschieben zurück. */
  before: Array<{ id: string; changes: TaskChanges }>
}

type MoveSource = Pick<DataSource, 'getTask' | 'getTasksByProject' | 'getSectionsByProject' | 'updateTasks'>

/**
 * **Verschieben** (#257): hängt die Aufgaben in ihrer bisherigen Reihenfolge ans Ende des Ziels —
 * wie `#Projekt /Abschnitt` im Titel. Die Unteraufgaben ziehen über `updateTasks` mit
 * (`moveChildrenWithParent`).
 *
 * Übergangen wird, was schon dort steht, und jede Unteraufgabe: Sie gehört zu ihrer Aufgabe und
 * hat für sich „Umhängen“ (#205). `null`, wenn nichts zu tun blieb.
 */
export async function moveTasks(source: MoveSource, ids: readonly string[], target: MoveTarget): Promise<MoveResult | null> {
  const rows = (await Promise.all(ids.map(id => source.getTask(id))))
    .filter((row): row is Task => !!row && !row.deletedAt && row.parentTaskId == null)
    .filter(row => row.projectId !== target.projectId || row.sectionId !== target.sectionId)
    .sort((a, b) => a.order - b.order)
  if (rows.length === 0) return null

  const [tasks, sections] = await Promise.all([
    source.getTasksByProject(target.projectId),
    source.getSectionsByProject(target.projectId),
  ])
  const movingIds = new Set(rows.map(row => row.id))
  const orders = computeAppendOrders({
    // Was gerade wandert, zählt am Ziel nicht mit — sonst hinge es sich hinter sich selbst.
    tasks: tasks.filter(task => !movingIds.has(task.id)),
    sections,
    sectionId: target.sectionId,
    count: rows.length,
  })

  await source.updateTasks(rows.map((row, i) => ({
    id: row.id,
    changes: { projectId: target.projectId, sectionId: target.sectionId, lifeSpace: target.lifeSpace, order: orders[i]! },
  })))

  return {
    moved: rows,
    before: rows.map(row => ({
      id: row.id,
      changes: { projectId: row.projectId, sectionId: row.sectionId, lifeSpace: row.lifeSpace, order: row.order },
    })),
  }
}
